/**
 * Mutex for refresh API to prevent parallel refresh calls.
 * Queues callbacks so they all receive the same refreshed token.
 */

type Resolver<T> = (value: T) => void;
type Rejector = (err: unknown) => void;

interface PendingRequest<T> {
  resolve: Resolver<T>;
  reject: Rejector;
}

class RefreshMutex {
  private isRefreshing = false;
  private pendingRequests: PendingRequest<unknown>[] = [];

  async run<T>(refreshFn: () => Promise<T>): Promise<T> {
    if (this.isRefreshing) {
      return new Promise<T>((resolve, reject) => {
        this.pendingRequests.push({
          resolve: (v) => resolve(v as T),
          reject,
        });
      });
    }

    this.isRefreshing = true;
    this.pendingRequests = [];

    try {
      const result = await refreshFn();
      this.pendingRequests.forEach(({ resolve }) => resolve(result));
      this.pendingRequests = [];
      return result;
    } catch (err) {
      this.pendingRequests.forEach(({ reject }) => reject(err));
      this.pendingRequests = [];
      throw err;
    } finally {
      this.isRefreshing = false;
    }
  }

  get isLocked(): boolean {
    return this.isRefreshing;
  }
}

export const refreshMutex = new RefreshMutex();
